import {
  DEAL_PAYROLL_STATUS, DealPayrollBreakdown, isDealPayrollIdentityMatch, isDealPayrollPeriodDiagnostic,
  isFinitePayrollNumber, isPlanfixDealPayroll, PAYROLL_CALCULATION_METHOD
} from '../models/deal-payroll.model';
import {CurrencyAmount} from '../models/payroll-currency.model';
import {Report} from '../models/report.model';
import {
  DEAL_BATCH_REQUEST_COUNT,
  DEAL_PAYROLL_GROUP_EMPTY_LENGTH,
  DEAL_PAYROLL_GROUP_OBJECT_TYPE,
  DEAL_PAYROLL_GROUP_STRING_TYPE,
  INDEX_NOT_FOUND,
  NO_BATCH_REQUESTS,
  VALID_PAYROLL_CALCULATION_METHODS
} from './deal-payroll-group-report.constants';
import {
  DealPayrollGroupMember,
  DealPayrollGroupRequestPlan,
  DealPayrollSelectionContext
} from './deal-payroll-group-report.types';

export {
  DealPayrollGroupMember,
  DealPayrollGroupRequestPlan,
  DealPayrollGroupUser
} from './deal-payroll-group-report.types';

export function buildDealPayrollGroupRequestPlan(
  users: DealPayrollGroupMember[]
): DealPayrollGroupRequestPlan {
  const dealUserIds: string[] = [];
  const legacyUsers: DealPayrollGroupMember[] = [];
  const seen = new Set<string>();
  (users || []).forEach((member: DealPayrollGroupMember) => {
    if (!member || !member.user || seen.has(member.user.id)) { return; }
    seen.add(member.user.id);
    if (isPlanfixDealPayroll(member.user)) {
      dealUserIds.push(member.user.id);
    } else {
      legacyUsers.push(member);
    }
  });
  const batchCount = dealUserIds.length ? DEAL_BATCH_REQUEST_COUNT : NO_BATCH_REQUESTS;
  return { dealUserIds, legacyUsers, requestCount: legacyUsers.length + batchCount };
}

export function selectExactDealPayrollRows(
  reports: Report[], dealUserIds: string[], expectedMonth: string
): Report[] {
  if (!validExpectedUserIds(dealUserIds) || !expectedMonth || !Array.isArray(reports)) {
    return null;
  }
  const context: DealPayrollSelectionContext = {
    expectedIds: dealUserIds, expectedMonth: expectedMonth, selectedIds: {}, selected: []
  };
  const valid = reports.every((report: Report) => collectExpectedReport(report, context));
  return valid && context.selected.length === dealUserIds.length ? context.selected : null;
}

export function selectMixedGroupReportRows(reports: Report[], expectedMonth: string): Report[] {
  if (!Array.isArray(reports)) {
    return null;
  }
  const seen = new Set<string>();
  return reports.every(report => validMixedReport(report, expectedMonth, seen)) ? reports : null;
}

function validMixedReport(report: Report, expectedMonth: string, seen: Set<string>): boolean {
  if (!validReportBody(report) || !validCalculationText(report.howSalaryCalculated) || seen.has(report.userID)) {
    return false;
  }
  const method = report.payrollCalculationMethod;
  if (method !== null && method !== undefined && VALID_PAYROLL_CALCULATION_METHODS.indexOf(method) === INDEX_NOT_FOUND) {
    return false;
  }
  seen.add(report.userID);
  return !isPlanfixDealPayroll(report) || validDealReportIdentity(report, expectedMonth)
    || isDealPayrollPeriodDiagnostic(report.userID, report.dealPayrollBreakdown);
}

function validCalculationText(calculations: string[]): boolean {
  return calculations === null || calculations === undefined || Array.isArray(calculations)
    && calculations.every(value => value === null || typeof value === DEAL_PAYROLL_GROUP_STRING_TYPE);
}

function collectExpectedReport(
  report: Report,
  context: DealPayrollSelectionContext
): boolean {
  if (!validReportRow(report)) {
    return false;
  }
  const userId = String(report.userID);
  if (context.expectedIds.indexOf(userId) === INDEX_NOT_FOUND) {
    return true;
  }
  if (context.selectedIds[userId] || !validDealReportIdentity(report, context.expectedMonth)) {
    return false;
  }
  context.selectedIds[userId] = true;
  context.selected.push(report);
  return true;
}

function validDealReportIdentity(report: Report, expectedMonth: string): boolean {
  return report.payrollCalculationMethod === PAYROLL_CALCULATION_METHOD.PLANFIX_DEAL
    && validDealBreakdown(report.dealPayrollBreakdown)
    && isDealPayrollIdentityMatch(report.userID, expectedMonth, report.dealPayrollBreakdown);
}

function validDealBreakdown(breakdown: DealPayrollBreakdown): boolean {
  return !!breakdown && validStatus(breakdown)
    && hasText(breakdown.currency) && hasText(breakdown.salaryCurrency)
    && Array.isArray(breakdown.finalSalaryAmounts)
    && breakdown.finalSalaryAmounts.every((amount: CurrencyAmount) => validCurrencyAmount(amount))
    && Array.isArray(breakdown.issues);
}

function validStatus(breakdown: DealPayrollBreakdown): boolean {
  return breakdown.status === DEAL_PAYROLL_STATUS.COMPLETE
    || breakdown.status === DEAL_PAYROLL_STATUS.INCOMPLETE
    || breakdown.status === DEAL_PAYROLL_STATUS.DISABLED;
}

function validCurrencyAmount(amount: CurrencyAmount): boolean {
  return !!amount && hasText(amount.currency) && isFinitePayrollNumber(amount.amount);
}

function validExpectedUserIds(userIds: string[]): boolean {
  if (!Array.isArray(userIds) || !userIds.length) {
    return false;
  }
  const seen: {[userId: string]: boolean} = {};
  return userIds.every((userId: string) => {
    if (!hasText(userId) || seen[userId]) {
      return false;
    }
    seen[userId] = true;
    return true;
  });
}

function validReportRow(report: Report): boolean {
  return validReportBody(report)
    && VALID_PAYROLL_CALCULATION_METHODS.indexOf(report.payrollCalculationMethod)
      !== INDEX_NOT_FOUND;
}

function validReportBody(report: Report): boolean {
  return !!report && hasText(report.userID)
    && !!report.summaryRecord && typeof report.summaryRecord === DEAL_PAYROLL_GROUP_OBJECT_TYPE
    && !Array.isArray(report.summaryRecord) && Array.isArray(report.records);
}

function hasText(value: string): boolean {
  return typeof value === DEAL_PAYROLL_GROUP_STRING_TYPE
    && value.trim().length > DEAL_PAYROLL_GROUP_EMPTY_LENGTH;
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/services/deal-payroll-group-report.adapter.ts